<template>
  <div
    class="flex w-full flex-col"
    :class="{ 'justify-between gap-y-1 lg:gap-y-4': reportHasAccess }"
  >
    <div
      v-if="reportHasAccess"
      class="container-button flex flex-row items-center justify-between gap-2"
      data-testid="services-group-buttons"
    >
      <CommonButton
        v-if="!videoIsValidate"
        class="mx-0 !w-full !cursor-default !text-sm lg:!min-w-[150px] 2xl:!max-w-[150px]"
        data-testid="esg-button-link"
        variant="primary"
        :show-spinner="false"
        disabled
      >
        Coming soon
      </CommonButton>
      <div v-if="videoIsValidate" class="flex gap-2">
        <CommonButton
          data-testid="esg-button-link"
          class="!h-fit !py-3"
          variant="primary"
          :link="link"
        >
          <div class="m-0 flex w-full flex-row items-center gap-x-1 p-0">
            <IconPlay class="h-4 w-4 flex-shrink-0 xl:hidden" />
            <span v-if="!hideLabel" class="hidden text-sm leading-4 xl:block">
              Watch Now
            </span>
          </div>
        </CommonButton>
        <ButtonDownload
          v-if="videoIsValidate"
          ref="downloadButton"
          class="!w-fit !border-2 !border-p-500 !bg-transparent !py-2.5"
          data-testid="esg-button-download"
          :data-filename="videoTitle"
          :download-file="vimeoData"
          text-classes="leading-4"
          variant="secondary"
          :show-label="false"
        />
      </div>
      <IconCheckCircle
        v-if="reportHasAccess"
        class="z-10 h-8 w-8 stroke-p-900"
      />
    </div>
    <div v-else class="flex items-end justify-between">
      <ButtonContactUs
        text="Contact Us"
        variant="clean"
        :services="initialFields"
        class="!text-sm !text-black"
      />
      <IconLockOutline class="z-10 h-8 w-8 stroke-p-900" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import CommonButton from '~/components/common/Button.vue'
import ButtonDownload from '~/components/shared/common/ButtonDownload.vue'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import IconPlay from '~/assets/icons/play-circle.svg'
import IconCheckCircle from '~/assets/icons/check-subscribe.svg'
import IconLockOutline from '~/assets/icons/LockOutline.svg'
import { EventBus } from '~/utils/eventBus'
import type { TReportProp } from '~/src/types/reports'
import { BUTTON_DOWNLOAD_ALL, SUSTAINABILITY } from '~/src/constants/markets'

const props = withDefaults(
  defineProps<{
    report?: TReportProp
    initialFields?: Array<string>
    product?: string
    hideLabel?: boolean
  }>(),
  {
    report: null,
    initialFields: () => [],
    product: '',
    hideLabel: false,
  },
)

const downloadButton = ref<HTMLElement | null>(null)
const videoTitle = computed(() => props.report?.title)
const reportId = computed(() => props.report?.id)
const vimeoData = computed(() => ({
  vimeoId: props.report?.file_name,
  isVideoVimeo: true,
}))
const videoIsValidate = computed(() => !!props.report?.file_name)
const reportHasAccess = computed(() => props.report?.has_access || false)
const link = computed(() => {
  const queryParams = window.location.search || ''
  let marketType = props.report?.market_type || ''
  if (marketType === 'esg') marketType = SUSTAINABILITY
  if (marketType === 'forecast') marketType = 'forecasts'
  if (!reportId.value || !marketType) return ''
  return `/${props.product}/data-reports/${marketType}/video/${reportId.value}${queryParams}`
})

const handlerDownload = () => {
  if (downloadButton.value) {
    downloadButton.value.click()
  }
}

onMounted(() => {
  /**
   * This method was added to be able to interact with global events because
   * it was necessary to listen for an event from a sibling component, and the
   * traditional way to listen for events is between parent and child components.
   *
   * What this does is add a listener of the clickButtonCommon event,
   * and when it detects that this event has been issued, it verifies
   * that its payload is equal to "forecasts-button-download-all" and
   * executes the function that downloads all the reports from
   * the current forecasts page.
   */
  EventBus.$on('clickButtonCommon', (_payload: string) => {
    if (_payload === BUTTON_DOWNLOAD_ALL) handlerDownload()
  })
})

/**
 * What this does are remove the listener from the clickButtonCommon event
 * before the component is destroyed.
 */
onBeforeUnmount(() => {
  EventBus.$off('clickButtonCommon')
})
</script>

<style scoped>
@media (width <= 359px) {
  .container-button {
    flex-direction: column;
    row-gap: 4px;
  }
}
</style>
